import { PANO_CUBE_FACE_MAX } from '@vrlearning/pano-maths';
import type { PanoCubeVariant, PanoLightVariant, PanoVariants } from '@vrlearning/lesson-schema';
import type { PanoCaps } from './pano-caps.js';

/**
 * Which file a 360° photo is shown from (ADR-041, contract C5): its KTX2 cube
 * and light copy when the API signed them, this build reads the cube's format
 * and the GPU takes it; else today's JPEG path. Pure; the prefetch plan asks
 * the same question, so a headset fetches what it will show.
 */

/** The cube formats this build uploads as they are: raw ASTC 6x6 (format C). */
export const PANO_CUBE_READS: readonly string[] = ['astc6x6'];

/**
 * The largest cube face taken. The cube path's GPU cost is counted at this
 * size (worlds/budget.ts, held under 96 MB by budget.test.ts): a larger face
 * from a later re-encode shows as the JPEG until both are raised together.
 */
export const PANO_CUBE_FACE_LIMIT = PANO_CUBE_FACE_MAX;

export type PanoSource =
  | {
      readonly kind: 'cube';
      readonly cube: PanoCubeVariant;
      readonly light: PanoLightVariant;
      /** The photo's JPEG, should the cube fail to fetch, read or go up. */
      readonly jpeg: string;
    }
  | { readonly kind: 'jpeg'; readonly url: string };

/**
 * The source for media id `id`, or `undefined` when the API did not sign it
 * (no JPEG URL: the photo cannot be had, whatever its variants say).
 */
export function panoSource(
  id: string,
  media: Readonly<Record<string, string>>,
  variants: Readonly<PanoVariants>,
  caps: PanoCaps,
): PanoSource | undefined {
  const jpeg = media[id];
  if (jpeg === undefined) return undefined;
  const variant = Object.hasOwn(variants, id) ? variants[id] : undefined;
  if (
    variant &&
    caps.astc &&
    PANO_CUBE_READS.includes(variant.cube.format) &&
    variant.cube.face <= caps.maxCubemapSize &&
    variant.cube.face <= PANO_CUBE_FACE_LIMIT
  ) {
    return { kind: 'cube', cube: variant.cube, light: variant.light, jpeg };
  }
  return { kind: 'jpeg', url: jpeg };
}
